/* eslint-disable */
/*!
  _   _  ___  ____  ___ ________  _   _   _   _ ___   
 | | | |/ _ \|  _ \|_ _|__  / _ \| \ | | | | | |_ _| 
 | |_| | | | | |_) || |  / / | | |  \| | | | | || | 
 |  _  | |_| |  _ < | | / /| |_| | |\  | | |_| || |
 |_| |_|\___/|_| \_\___/____\___/|_| \_|  \___/|___|
                                                                                                                                                                                                                                                                                                                                       
=========================================================
* Horizon UI - v1.1.0
=========================================================

* Product Page: https://www.horizon-ui.com/
* Copyright 2022 Horizon UI (https://www.horizon-ui.com/)

* Designed and Coded by Simmmple

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import React, { ChangeEvent, FormEvent, useState } from "react";
// import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { Redirect, useLocation } from "react-router-dom";
// Chakra imports
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  Image,
} from "@chakra-ui/react";
// Custom components
import DefaultAuth from "layouts/auth/Default";
import logoLight from "assets/img/logo/identite-login.png";
import logoDark from "assets/img/logo/identite-login-dark.png";
// Assets
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { useUserAuth } from "auth/auth";



function SignIn() {
  // Chakra color mode
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const logo = useColorModeValue(logoLight, logoDark);

  const [show, setShow] = React.useState(false);
  const handleClick = () => setShow(!show);
  
  const [emailInput, setEmailInput] = React.useState('');
  const [passwordInput, setPasswordInput] = React.useState('')
  const handleChangeEmail = (event:ChangeEvent<HTMLInputElement>) => setEmailInput(event.target.value);
  const handleChangePassword = (event:ChangeEvent<HTMLInputElement>) => setPasswordInput(event.target.value)
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Auth Firebase
  const { logIn, user } = useUserAuth();
  // Page demandée avant la redirection vers la connexion (voir ProtectedRoute)
  const location = useLocation<{ from?: { pathname: string; search?: string } }>();
  const from = location.state?.from;
  const redirectTo = from ? `${from.pathname}${from.search || ""}` : "/admin";
  // Soumission du formulaire : bouton "Se connecter" ou touche Entrée dans un champ
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setError("");
    try {
      await logIn(emailInput, passwordInput);
      // En cas de succès, la redirection démonte la page : pas de remise à zéro ici
    } catch (err:any) {
      setError(err.message);
      setIsSubmitting(false);
    }
  }
  return (
    <>
    {user && <Redirect to={redirectTo} />}
    <DefaultAuth illustrationBackground={''} image={''}>
      <Flex
        maxW={{ base: "100%" }}
        w='100%'
        mx={{ base: "auto", lg: "0px" }}
        me='auto'
        h='100%'
        alignItems='center'
        justifyContent='center'
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        mt={{ base: "40px", md: "14vh" }}
        flexDirection='column'>
        <Box mb={{ base: "24px", md: "32px" }}>
          {/* Logo exporté en 480 px de large (2x) pour rester net sur écran Retina */}
          <Image
            src={logo}
            alt='jonasmillet.com'
            w={{ base: "200px", md: "240px" }}
            h='auto'
            htmlWidth='480'
            htmlHeight='330'
          />
        </Box>
        <Flex
          zIndex='2'
          direction='column'
          w={{ base: "100%", md: "420px" }}
          maxW='100%'
          background='transparent'
          borderRadius='15px'
          mx={{ base: "auto", lg: "unset" }}
          me='auto'
          mb={{ base: "20px", md: "auto" }}>
          <form onSubmit={handleSubmit}>
            <FormControl>
              <FormLabel
                display='flex'
                ms='4px'
                fontSize='sm'
                fontWeight='500'
                color={textColor}
                mb='8px'>
                Email<Text color={brandStars}>*</Text>
              </FormLabel>
              <Input
                isRequired={true}
                variant='auth'
                fontSize='sm'
                ms={{ base: "0px", md: "0px" }}
                type='email'
                placeholder='mail@simmmple.com'
                mb='24px'
                fontWeight='500'
                size='lg'
                onChange={handleChangeEmail}
                value={emailInput}
              />
              <FormLabel
                ms='4px'
                fontSize='sm'
                fontWeight='500'
                color={textColor}
                display='flex'>
                Password<Text color={brandStars}>*</Text>
              </FormLabel>
              <InputGroup size='md'>
                <Input
                  isRequired={true}
                  fontSize='sm'
                  placeholder='Min. 8 characters'
                  mb='24px'
                  size='lg'
                  type={show ? "text" : "password"}
                  variant='auth'
                  onChange={handleChangePassword}
                  value={passwordInput}
                />
                <InputRightElement display='flex' alignItems='center' mt='4px'>
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: "pointer" }}
                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={handleClick}
                  />
                </InputRightElement>
              </InputGroup>
              <Button
                fontSize='sm'
                colorScheme="teal"
                fontWeight='500'
                w='100%'
                h='50'
                mb='24px'
                type='submit'
                isLoading={isSubmitting}
                >
                Se connecter
              </Button>
            </FormControl>
          </form>
          <Flex
            flexDirection='column'
            justifyContent='center'
            alignItems='start'
            maxW='100%'
            mt='0px'>
            {error &&
            <Alert status='error'>
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            }
          </Flex>
        </Flex>
      </Flex>
    </DefaultAuth>
    </>
  );
}

export default SignIn;
